<template>
  <Field
    v-if="pattern?.length"
    ref="providerRef"
    v-slot="{ errors, field }"
    :name="type"
    :vid="type"
    :rules="validationRules"
  >
    <b-field
      :type="errors[0] ? 'is-danger' : ''"
      :message="errors[0] ? $t('rules.invalid-date-format', { format: pattern }) : ''"
    >
      <b-input
        v-input-mask="mask"
        :value="formattedValue"
        :disabled="disabled"
        :aria-label="placeholder"
        :placeholder="placeholder"
        :maxlength="mask.length"
        @update:model-value="(val: string | number | undefined) => {
          formattedValue = String(val ?? '');
          field.onInput(val);
          if (String(val ?? '').length === mask.length) onValidateAndEmit();
          else if (providerRef) providerRef.setErrors([]);
        }"
        @blur="(e?: Event) => { if (e) field.onBlur(e); onValidateAndEmit();}"
        @keyup.enter="onValidateAndEmit"
      />
    </b-field>
  </Field>
</template>

<script lang="ts">
import { ref, computed, Ref, watch } from "vue";
import { useDateFormatting } from "@/composable/components/dateFormatting";
import { Field, FieldContext } from "vee-validate";
import { BField, BInput } from "buefy";

export default {
  name: "DatePatternInput",
  components: { BInput, BField, Field },
  props: {
    value: {
      type: [String, Date],
      default: "",
    },
    disabled: { type: Boolean, default: false },
    // #476 P3 - Champ optionnel dans les filtres, obligatoire ailleurs (defaut true)
    isRequired: { type: Boolean, default: true },
    pattern: { type: String, required: true },
    type: { type: String, default: "start" },
  },
  emits: ["input"],
  setup(props, { emit }) {
    const providerRef: Ref<FieldContext | null> = ref(null);

    const dateUtils = computed(() => useDateFormatting().parsePattern(props.pattern));
    const formattedValue = ref(props.value ? dateUtils.value.formatted(props.value) : "");
    const placeholder = computed(() => dateUtils.value.example);
    const mask = computed(() => dateUtils.value.mask);
    // #476 - Ancien message d'erreur brut (regex illisible) : :message="errors[0]"
    // Remplace par : $t('rules.invalid-date-format', { format: pattern })
    // #476 P3 - Quand isRequired=false (filtres), la regex accepte aussi les valeurs vides.
    // On prefixe le pattern avec ^$| pour matcher chaine vide OU format date valide.
    const baseRegexp = dateUtils.value.regexp.source;
    const optionalRegexp = "^$|" + baseRegexp;
    const validationRules = ref(
      props.isRequired
        ? "required|regexp:" + baseRegexp.replaceAll("|", "ESCAPED_OR")
        : "regexp:" + optionalRegexp.replaceAll("|", "ESCAPED_OR"),
    );
    watch(
      () => props.value,
      (newVal) => {
        formattedValue.value = newVal
          ? dateUtils.value.formatted(newVal)
          : "";
      },
      { immediate: true },
    );

    const onValidateAndEmit = async () => {
      if (!providerRef.value) {
        return;
      }
      const valid = await providerRef.value.validate();
      if (valid) {
        emit("input", formattedValue.value);
      }
      else {
        // #476 - Vider le champ si la valeur est incomplete ou invalide au blur
        // Avant : la valeur incomplete restait affichee avec un message d'erreur regex,
        // ou le masque la completait avec une date aleatoire (ex: "2" -> "2001-02-01")
        formattedValue.value = "";
      }
    };

    return {
      formattedValue,
      placeholder,
      validationRules,
      onValidateAndEmit,
      providerRef,
      dateUtils,
      mask,
    };
  },
};
</script>
